/** @jsx jsx */

import { useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import EditMarker from 'components/UI/EditMarker';
import { colors } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { State } from 'store/store';
import { getBaseButtonStyles } from './getBaseButtonStyles';
import {
  getFocusColorForCSSObject,
  updateButtonHeightForIE,
} from './buttonHelpers';
import hexToRgb from 'services/hexToRGB';
import { speechSynthesisCancel } from 'services/speechSynthesisCancel';
import { removeUndefinedEntriesFromObject } from 'services/removeUndefinedEntriesFromObject';
import {
  getDefaultButtonStyles as getThemeDefaultButtonStyles,
  TThemeButtonsDefaultStyles,
} from 'store/theme';
import { useModuleVersionOnCreation } from 'services/useModuleVersionOnCreation';

const { lPink } = colors;

const GenericButton = props => {
  //
  const moduleVersionOnCreation = useModuleVersionOnCreation();
  const themeButtonDefault = getThemeDefaultButtonStyles(
    moduleVersionOnCreation
  );

  const { focusedNodeId, theme } = useSelector((state: State) => {
    const { focusedNodeId, theme } = state;
    return {
      focusedNodeId,
      theme,
    };
  });

  const themeButtons = theme.buttons;

  const cleanedBlockProps = removeUndefinedEntriesFromObject(props);

  const { id, label, enabled = undefined, onClick } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    textColor,
    backgroundColor,
    fontFamily,
    shape,
    borderRadius,
    shadow,
    outline,
    outlineColor,
    backgroundColorOnHover,
    textColorOnHover,
    useLargeButton,
    focusColor,
    disabledColor,
    // includeButtonBackground,
    backgroundOpacity,
    backgroundOpacityOnHover,
    fontSize,
    bold,
    uppercase,
  } = {
    ...themeButtonDefault,
    ...themeButtons.main,
    ...cleanedBlockProps,
  } as TThemeButtonsDefaultStyles & {
    disabledColor?: string | undefined;
  };

  const selected = id === focusedNodeId;
  const disabled = enabled !== undefined ? !enabled : false;

  let buttonShape = shape;

  const baseButtonStyles = getBaseButtonStyles({
    color: textColor,
    background: backgroundColor,
    fontFamily,
    buttonShape,
    borderRadius,
    buttonShadow: shadow,
    buttonOutline: outline,
    hoverColor: backgroundColorOnHover,
    useLargeButton,
  }) as any;

  const _focusColor_ = getFocusColorForCSSObject(focusColor || backgroundColor);

  const { r, g, b } = hexToRgb(backgroundColor);

  let a = backgroundOpacity / 100;
  let backgroundColorRGBA = `rgba(${r}, ${g}, ${b}, ${a})`;

  const { r: hoverR, g: hoverG, b: hoverB } = hexToRgb(
    backgroundColorOnHover || backgroundColor
  );

  let hoverA = (backgroundOpacityOnHover ?? 80) / 100;

  let backgroundColorOnHoverRGBA = `rgba(${hoverR}, ${hoverG}, ${hoverB}, ${hoverA})`;

  const genericButton = (
    <button
      ref={buttonRef}
      id={`generic-button-${id}`}
      type="button"
      css={css({
        ...baseButtonStyles,
        fontSize,
        ...(bold && {
          fontWeight: 700,
        }),
        ...(uppercase && {
          textTransform: 'uppercase',
        }),

        background: backgroundColorRGBA,

        border: `1px solid ${
          outline ? outlineColor || backgroundColor : 'transparent'
        }`,

        transition: 'all 0.15s',

        ':hover': {
          color: `${textColorOnHover} !important`,
          background: backgroundColorOnHoverRGBA,
        },

        ...(!window.dev() && disabled
          ? {
              // potentially want to be able to customize disabled color ?
              pointerEvents: 'none',
              opacity: 0.25,
              ...(disabledColor && {
                background: disabledColor,
                opacity: 1,
              }),
            }
          : {
              ':focus-visible': {
                boxShadow: _focusColor_,
              },
            }),
      })}
      disabled={disabled}
      onClick={() => {
        speechSynthesisCancel();
        onClick && onClick();
      }}
    >
      {label}
    </button>
  );

  updateButtonHeightForIE(buttonRef.current, buttonShape);

  return window.dev() ? (
    <div
      css={css({
        position: 'relative',
        display: 'inline-flex',

        ':hover': {
          button: {
            background: backgroundColorOnHoverRGBA,

            color: `${textColorOnHover} !important`,
          },
        },
      })}
    >
      {genericButton}

      <div
        css={css({
          position: 'absolute',
          left: -15,
          top: -10,
          width: 'calc(100% + 30px)',
          height: 'calc(100% + 20px)',
          boxShadow: selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey',
          transition: 'all 0.15s',
          cursor: 'pointer',

          ':hover': {
            boxShadow: `0 0 0 1px ${lPink}`,
          },
        })}
        onClick={e => {
          if (selected) {
            return;
          }

          setCurrentBlock({ id });
        }}
      >
        {!selected && <EditMarker />}
      </div>
    </div>
  ) : (
    genericButton
  );
};

export { GenericButton };
